import * as THREE from 'three';
import { applyLuxuryMaterials } from './luxuryMaterials';
import { finishAlbedo, PREMIUM_BLACK_ALBEDO } from './finishAppearance';

const FINISH_IDS = ['xs-5200416', 'xs-2217', 'xs-9a598', 'xs-7323128', 'dc-f83513', 'dc-f83376', 'dc-f84001', 'dc-f84000'];

export function createMaterialLibrary(renderer, invalidate, { roomTheme } = {}) {
  const software = renderer.userData.software;
  const textures = new Set(), materials = new Set(), failures = [];
  let ready = false;
  const manager = new THREE.LoadingManager();
  manager.onLoad = () => { ready = true; invalidate(); };
  manager.onError = url => { failures.push(url); console.error('Material texture failed to load:', url); invalidate(); };
  const loader = new THREE.TextureLoader(manager);
  const anisotropy = Math.min(renderer.capabilities.getMaxAnisotropy(), renderer.userData.software ? 2 : 8);
  const load = (name, color = false) => {
    const texture = loader.load(`${process.env.PUBLIC_URL}/textures/${name}`, invalidate);
    texture.name = name; texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    texture.colorSpace = color ? THREE.SRGBColorSpace : THREE.NoColorSpace;
    texture.anisotropy = anisotropy; textures.add(texture); return texture;
  };
  const own = (material, role, tileSize = .022) => {
    material.name = `showroom-${role}`;
    material.userData = { ...material.userData, managed: true, role, tileSize };
    materials.add(material); return material;
  };
  const mineral = { map: load('mineral-color.webp', true), normalMap: load('mineral-normal.png'), roughnessMap: load('mineral-roughness.webp') };
  const brushed = { map: load('brushed-metal-detail.webp', true), normalMap: load('brushed-metal-normal.png'), roughnessMap: load('brushed-metal-roughness.webp') };
  const coatings = Object.fromEntries(FINISH_IDS.map(id => [id, {
    map: load(`${id}-detail.webp`, true), normalMap: load(`${id}-normal.png`), roughnessMap: load(`${id}-roughness.webp`),
  }]));
  const floor = own(new THREE.MeshPhysicalMaterial({ ...mineral, color: '#f3f5f3', metalness: 0, roughness: .79, normalScale: new THREE.Vector2(.38, .38), clearcoat: software ? 0 : .13, clearcoatRoughness: .55, envMapIntensity: .75 }), 'floor', .6);
  const floorEdge = own(new THREE.MeshStandardMaterial({ ...mineral, color: '#adb3af', metalness: 0, roughness: 1, normalScale: new THREE.Vector2(.55, .55), envMapIntensity: .35 }), 'floor-edge', .6);
  const wall = own(new THREE.MeshStandardMaterial({ color: '#c6ccc6', normalMap: mineral.normalMap, roughnessMap: mineral.roughnessMap, normalScale: new THREE.Vector2(.14, .14), roughness: 1 }), 'wall', .9);
  const darkMetal = own(new THREE.MeshStandardMaterial({ color: '#353d3b', ...coatings['xs-5200416'], normalScale: new THREE.Vector2(.2, .2), roughness: .72, metalness: .2 }), 'frame');
  const roomMaterials = roomTheme === 'luxury'
    ? applyLuxuryMaterials({ floor, floorEdge, wall, darkMetal }, { load, own, software }) : {};
  const steel = own(new THREE.MeshPhysicalMaterial({ ...brushed, color: '#c8d0ce', metalness: .98, roughness: .36, normalScale: new THREE.Vector2(.32, .32), anisotropy: software ? 0 : .62, envMapIntensity: .95 }), 'steel');
  const cutAluminium = own(new THREE.MeshPhysicalMaterial({ ...brushed, color: '#bdc4c1', metalness: .86, roughness: .62, normalScale: new THREE.Vector2(.42, .42), anisotropy: software ? 0 : .35, envMapIntensity: .7 }), 'cut-metal');
  const gasket = own(new THREE.MeshStandardMaterial({ color: '#222724', normalMap: coatings['xs-5200416'].normalMap, normalScale: new THREE.Vector2(.4, .4), roughness: .94, metalness: 0 }), 'gasket', .018);
  const thermal = own(new THREE.MeshStandardMaterial({ color: '#47544d', normalMap: mineral.normalMap, normalScale: new THREE.Vector2(.1, .1), roughness: .94, metalness: 0 }), 'thermal', .018);
  const glass = own(new THREE.MeshPhysicalMaterial({
    color: '#e2f0e9', metalness: 0, roughness: .035, transmission: software ? 0 : .94,
    thickness: .006, ior: 1.52, attenuationColor: new THREE.Color('#c2dfd4'),
    attenuationDistance: 2.5, transparent: !!software, opacity: software ? .18 : 1,
    depthWrite: !software,
    side: THREE.DoubleSide, clearcoat: .16, clearcoatRoughness: .045,
    specularIntensity: 1, envMapIntensity: 1.05,
  }), 'glass');
  glass.forceSinglePass = software;
  const glassEdge = own(new THREE.LineBasicMaterial({ color: '#8caba0', transparent: true, opacity: .4, depthWrite: false }), 'glass-edge');
  const applyFinish = (frame, finish) => {
    if (!finish || !coatings[finish.id]) return;
    const silver = finish.id === 'dc-f83513', fluorocarbon = finish.id.startsWith('dc-') && !silver;
    frame.color.set(finishAlbedo(finish));
    Object.assign(frame, coatings[finish.id]);
    frame.metalness = silver ? .78 : .08;
    frame.roughness = silver ? .5 : fluorocarbon ? .61 : .76;
    frame.clearcoat = silver ? .06 : fluorocarbon ? .24 : .1;
    frame.clearcoatRoughness = fluorocarbon ? .26 : .44;
    frame.anisotropy = silver && !software ? .4 : 0;
    frame.normalScale.setScalar(fluorocarbon ? .15 : .28);
    frame.userData.finishId = finish.id;
    frame.needsUpdate = true;
  };
  const makeFrame = () => {
    const frame = own(new THREE.MeshPhysicalMaterial({ ...coatings['xs-5200416'], color: PREMIUM_BLACK_ALBEDO, normalScale: new THREE.Vector2(.28, .28), roughness: .76, metalness: .08, clearcoat: .1, clearcoatRoughness: .44, envMapIntensity: .85 }), 'frame');
    return frame;
  };
  return {
    ...roomMaterials, floor, floorEdge, wall, darkMetal, steel, cutAluminium, gasket, thermal, glass, glassEdge, makeFrame, applyFinish, software,
    setPhotoMode: active => {
      if (!!glass.userData.photoMode === active) return;
      glass.userData.photoMode = active;
      // Physical transmission samples only the WebGL scene, not the HTML photo.
      // A reflective transparent layer lets the authentic house show through.
      glass.transmission = active || software ? 0 : .94;
      glass.transparent = active || software; glass.opacity = active ? .08 : software ? .18 : 1;
      glass.depthWrite = !active && !software; glass.forceSinglePass = active || software; glass.needsUpdate = true;
    },
    get status() { return failures.length ? 'error' : ready ? 'ready' : 'loading'; },
    get textureCount() { return textures.size; },
    get failures() { return [...failures]; },
    dispose: () => { materials.forEach(m => m.dispose()); textures.forEach(t => t.dispose()); },
  };
}